/**
 * RBAC Permissions Hook - Phase 5 Implementation
 * Uses VesselContext for scope, base keys for storage
 * Implements minimal role matrix: crew, master, owner, admin
 */

import { useCallback, useMemo } from 'react';
import { useVessel } from '../contexts/VesselContext';
import { useStorageState } from './useStorageState';

// Simplified 3-role maritime system
const ROLE_MATRIX = {
  // Regular crew - view and complete own tasks
  crew: {
    '*': ['read'], // Read everything by default
    hours_of_rest: ['read', 'update:own'], // Record own hours only
    maintenance: ['read', 'complete'], // Can complete assigned tasks
    defects: ['read', 'create'], // Can report defects
    incidents: ['read', 'create'], // Can report incidents
    documents: ['read', 'acknowledge'], // Can mark documents as read
    familiarisation: ['read', 'complete:own'], // Complete own familiarisation
    certificates: ['read'] // View own certificates
  },
  
  // Vessel master/captain - full operational control on assigned vessels
  skipper: {
    '*': ['read'], // Read everything
    crew: ['read', 'create', 'update', 'delete'], // Manage crew - skipper can delete
    hours_of_rest: ['read', 'create', 'update', 'sign'],
    drills: ['read', 'create', 'update', 'sign'],
    maintenance: ['read', 'create', 'update', 'sign'],
    defects: ['read', 'create', 'update', 'close'],
    incidents: ['read', 'create', 'update', 'investigate', 'close'],
    risk_assessments: ['read', 'create', 'update', 'sign'],
    log_entries: ['read', 'create', 'update', 'sign'],
    voyages: ['read', 'create', 'update', 'sign'],
    reports: ['read', 'generate', 'export'],
    vessel: ['switch'], // Can switch between assigned vessels
    policies: ['read', 'upload', 'update'], // Can manage documents
    training: ['read', 'create', 'update', 'sign'],
    certificates: ['read', 'create', 'update'], // Issue and manage certificates
    // Operations module permissions (added 2025-01-15 - RBAC audit fix)
    checklists: ['read', 'create', 'update', 'delete', 'execute'], // Operational checklists
    permits: ['read', 'create', 'update', 'approve', 'close'],     // Work permits (WBC3: Master approves)
    bunkering: ['read', 'create', 'update'],                        // Fuel bunkering logs
    templates: ['read', 'create', 'update', 'delete'],              // Custom checklist templates
    settings: ['read', 'update']                                    // Module settings
  },
  
  // Company/fleet admin - full control across all vessels
  fleet: {
    '*': ['*'], // All permissions everywhere
    fleet: ['read_all', 'write_all', 'assign_roles', 'manage_vessels'],
    vessel: ['create', 'read', 'update', 'delete'] // Explicit vessel management
  },

  // Super admin (Jonathan) - can do anything + manage organizations
  super_admin: {
    '*': ['*'], // All permissions everywhere
    organizations: ['read', 'create', 'update', 'delete', 'impersonate'],
    users: ['read', 'create', 'update', 'delete', 'impersonate'],
    audit: ['read', 'export'],
    support: ['impersonate', 'view_all_orgs']
  }
};

// Fleet permissions - who can access vessels they're not assigned to
const FLEET_PERMISSIONS = {
  super_admin: ['fleet:read_all', 'fleet:write_all', 'org:read_all', 'org:write_all'],
  fleet: ['fleet:read_all', 'fleet:write_all'], // Fleet role has full access to all vessels
  skipper: [], // Skippers only see assigned vessels
  crew: [] // Crew only see assigned vessels
};

// Default user - safest permissions in production
const DEFAULT_USER = {
  id: 'USR-ANON',
  name: 'Anonymous User',
  role: process.env.NODE_ENV === 'development' ? 'skipper' : 'crew', // Safe default: crew in production
  crewId: null,
  vesselIds: [] // Vessels the user is assigned to
};

export const usePermissions = () => {
  const { vessel } = useVessel();
  const currentVesselId = vessel?.id;
  
  // Use storage hook for current user - NOT vessel-scoped (users exist across fleet)
  const { value: storedUser } = useStorageState('current_user', DEFAULT_USER, { noScope: true });
  
  // Parse user if it's a string
  const currentUser = useMemo(() => {
    if (typeof storedUser === 'string') {
      try {
        return JSON.parse(storedUser);
      } catch (e) {

        return DEFAULT_USER;
      }
    }
    return storedUser || DEFAULT_USER;
  }, [storedUser]);
  
  /**
   * Check if user can access a specific vessel
   * @param {string} vesselId - Vessel to check
   * @param {boolean} write - Whether write access is needed
   * @returns {boolean} - Whether access is allowed
   */
  const vesselAllowed = useCallback((vesselId, write = false) => {
    if (!vesselId) return false;
    
    const { role, vesselIds = [] } = currentUser || {};
    
    // Check fleet permissions
    const fleetPerms = FLEET_PERMISSIONS[role] || [];
    
    // Fleet role with fleet:read_all can access any vessel
    if (fleetPerms.includes('fleet:read_all')) {
      // For write, check if they have write_all permission
      if (write && !fleetPerms.includes('fleet:write_all')) {
        // Only fleet role has write_all
        return role === 'fleet';
      }
      return true;
    }
    
    // Check if user is assigned to this vessel
    // For backward compatibility, if vesselIds is empty, allow current vessel
    if (vesselIds.length === 0) {
      return vesselId === currentVesselId;
    }
    
    return vesselIds.includes(vesselId);
  }, [currentUser, currentVesselId]);
  
  /**
   * Check if user can perform action on resource
   * @param {string} action - Action to perform (read, create, update, delete, sign, export)
   * @param {string} resource - Resource type (hours, drills, crew, etc.)
   * @param {object} ctx - Context object with additional info (e.g., ownerId, vesselId)
   * @returns {boolean} - Whether action is allowed
   */
  const can = useCallback((action, resource, ctx = {}) => {
    // Log permission checks in development
    if (process.env.NODE_ENV === 'development') {

    }
    
    // No user = no permissions
    if (!currentUser || !currentUser.role) {

      return false;
    }
    
    const { role } = currentUser;
    
    // Check vessel assignment first (unless fleet role)
    const targetVesselId = ctx.vesselId || currentVesselId;
    if (targetVesselId && role !== 'fleet') {
      const isWrite = action !== 'read';
      if (!vesselAllowed(targetVesselId, isWrite)) {

        return false;
      }
    }
    
    // Fleet and super_admin roles can do anything on any vessel
    if (role === 'fleet' || role === 'super_admin') {
      return true;
    }
    
    // Check if role has permissions for this resource
    const rolePerms = ROLE_MATRIX[role];
    if (!rolePerms) {

      return false;
    }
    
    // Check wildcard permissions
    if (rolePerms['*']) {
      if (rolePerms['*'].includes('*')) return true;
      if (rolePerms['*'].includes(action)) return true;
    }
    
    // Get permissions for specific resource
    const resourcePerms = rolePerms[resource];
    if (!resourcePerms || resourcePerms.length === 0) {
      // Check if wildcard gives read permission
      if (action === 'read' && rolePerms['*'] && rolePerms['*'].includes('read')) {
        return true;
      }
      return false;
    }
    
    // Check if action is allowed
    const fullAction = ctx.own ? `${action}:own` : action;
    
    // Check both specific and general action
    const allowed = resourcePerms.includes(fullAction) || 
                   resourcePerms.includes(action) ||
                   resourcePerms.includes('*');
    
    // For "own" actions, verify ownership
    if (allowed && fullAction.includes(':own')) {
      if (ctx.ownerId && ctx.ownerId !== currentUser.id && ctx.ownerId !== currentUser.crewId) {
        return false;
      }
    }
    
    return allowed;
  }, [currentUser, currentVesselId, vesselAllowed]);
  
  const hasRole = useCallback((role) => {
    return currentUser && currentUser.role === role;
  }, [currentUser]);
  
  const hasAnyRole = useCallback((roles) => {
    return currentUser && roles.includes(currentUser.role);
  }, [currentUser]);
  
  const hasAllRoles = useCallback(() => {
    // Single user can't have multiple roles simultaneously
    return false;
  }, []);
  
  /**
   * Get UI message for denied actions
   */
  const getDeniedMessage = useCallback((action, resource) => {
    // Map resource names for better messages
    const resourceLabels = {
      hours_of_rest: 'hours of rest',
      drills: 'drills',
      crew: 'crew',
      maintenance: 'maintenance',
      defects: 'defects',
      incidents: 'incidents'
    };
    
    const resourceLabel = resourceLabels[resource] || resource;
    
    const messages = {
      sign: `Only masters can sign ${resourceLabel}`,
      delete: `You don't have permission to delete ${resourceLabel}`,
      update: `You don't have permission to edit ${resourceLabel}`,
      create: `You don't have permission to create ${resourceLabel}`,
      export: `You don't have permission to export ${resourceLabel}`
    };
    
    return messages[action] || `Permission denied: ${action} on ${resourceLabel}`;
  }, []);
  
  return {
    can,
    vesselAllowed,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    getDeniedMessage,
    currentUser,
    currentRole: currentUser?.role || 'crew',
    isSuperAdmin: currentUser?.role === 'super_admin',  // NEW
    isFleet: currentUser?.role === 'fleet' || currentUser?.role === 'super_admin',  // MODIFIED
    isSkipper: currentUser?.role === 'skipper',
    isCrew: currentUser?.role === 'crew',
    // Legacy support - map old roles to new ones
    isAdmin: currentUser?.role === 'fleet' || currentUser?.role === 'super_admin',  // MODIFIED
    isMaster: currentUser?.role === 'skipper',
    isOfficer: currentUser?.role === 'skipper',
    isOwner: currentUser?.role === 'fleet' || currentUser?.role === 'super_admin',  // MODIFIED
    assignedVessels: currentUser?.vesselIds || [],
    hasFleetAccess: FLEET_PERMISSIONS[currentUser?.role]?.includes('fleet:read_all') || false
  };
};

// Export role matrix for testing
export const getRoleMatrix = () => ROLE_MATRIX;